import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LABELS = {
  no: {
    open: "Chat med Volt",
    title: "Volt – Canal Boats-assistenten",
    sub: "Spør om båter, ruter eller priser",
    placeholder: "Skriv din melding…",
    send: "Send",
    hello: "Hei! Jeg er Volt, Canal Boats-assistenten. Hvordan kan jeg hjelpe deg i dag?",
    loggedOutHint: "Du er ikke innlogget – generelle spørsmål besvares her, mens dine egne bookinger krever innlogging.",
  },
  en: {
    open: "Chat with Volt",
    title: "Volt – Canal Boats Assistant",
    sub: "Ask about boats, routes or pricing",
    placeholder: "Type your message…",
    send: "Send",
    hello: "Hi! I'm Volt, the Canal Boats assistant. How can I help you today?",
    loggedOutHint: "You're not signed in – general questions are answered here, personal booking details require login.",
  },
  de: {
    open: "Chat mit Volt",
    title: "Volt – Canal Boats Assistent",
    sub: "Fragen zu Yachten, Routen oder Preisen",
    placeholder: "Nachricht eingeben…",
    send: "Senden",
    hello: "Hallo! Ich bin Volt, der Canal Boats-Assistent. Wie kann ich Ihnen helfen?",
    loggedOutHint: "Sie sind nicht angemeldet – allgemeine Fragen werden hier beantwortet; persönliche Buchungsdetails erfordern eine Anmeldung.",
  },
};

// Flytende chat-widget. Brukes på marketing-sidene og i portalen –
// skiller automatisk mellom innlogget og uinnlogget via auth-cookien.
// Språket styres eksternt (prop `lang`), f.eks. av marketing-i18n eller
// portalens språkvalg.
export default function ChatWidget({ lang = "no", page, hidden = false }) {
  const l = LABELS[lang] || LABELS.no;
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", content: l.hello }]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(() => {
    try { return localStorage.getItem("cbt:chat_session") || null; } catch { return null; }
  });
  const bottomRef = useRef(null);

  // Oppdater hilsen hvis språk endres
  useEffect(() => {
    setMessages((prev) =>
      prev.length === 1 && prev[0].role === "assistant"
        ? [{ role: "assistant", content: l.hello }]
        : prev,
    );
  }, [lang]); // eslint-disable-line

  useEffect(() => {
    if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: "smooth" });
  }, [messages, sending]);

  const send = async (e) => {
    e?.preventDefault?.();
    const text = input.trim();
    if (!text || sending) return;
    setMessages((m) => [...m, { role: "user", content: text }]);
    setInput("");
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/chat/send`, {
        session_id: sessionId,
        message: text,
        lang,
        page: page || (typeof window !== "undefined" ? window.location.pathname : null),
      });
      if (data.session_id && data.session_id !== sessionId) {
        setSessionId(data.session_id);
        try { localStorage.setItem("cbt:chat_session", data.session_id); } catch { /* noop */ }
      }
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (err) {
      setMessages((m) => [...m, {
        role: "assistant",
        content: {
          no: "Beklager – jeg klarte ikke å svare akkurat nå. Prøv igjen eller send e-post til post@canalboats.no.",
          en: "Sorry — I couldn't respond. Please try again or email post@canalboats.no.",
          de: "Entschuldigung – ich konnte gerade nicht antworten. Bitte erneut versuchen oder an post@canalboats.no schreiben.",
        }[lang] || "Error",
      }]);
    } finally {
      setSending(false);
    }
  };

  if (hidden) return null;

  return (
    <>
      {!open && (
        <button
          type="button"
          data-testid="chat-widget-open"
          onClick={() => setOpen(true)}
          className="fixed bottom-5 right-5 z-50 h-14 w-14 rounded-full bg-[#A57F39] hover:bg-[#BD984D] text-white shadow-2xl flex items-center justify-center transition hover:scale-105"
          aria-label={l.open}
        >
          <MessageCircle className="h-6 w-6" />
          <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-emerald-400 ring-2 ring-white animate-pulse" />
        </button>
      )}

      {open && (
        <div
          data-testid="chat-widget-panel"
          className="fixed bottom-5 right-5 z-50 w-[min(92vw,360px)] h-[min(72vh,540px)] rounded-2xl shadow-2xl bg-white border border-[#A57F39]/30 flex flex-col overflow-hidden"
        >
          {/* Header */}
          <div className="bg-[#0F1A2B] text-white px-4 py-3 flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.32em] text-[#ECC974]">{l.sub}</div>
              <div className="text-sm font-semibold mt-0.5">{l.title}</div>
            </div>
            <button
              type="button"
              data-testid="chat-widget-close"
              onClick={() => setOpen(false)}
              className="h-8 w-8 rounded-full hover:bg-white/10 flex items-center justify-center"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2 bg-[#F8F7F2]" data-testid="chat-widget-messages">
            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[82%] rounded-2xl px-3 py-2 text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-[#A57F39] text-white rounded-br-sm"
                      : "bg-white text-[#0F1A2B] border border-[#A57F39]/15 rounded-bl-sm"
                  }`}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-white text-[#0F1A2B] border border-[#A57F39]/15 rounded-2xl rounded-bl-sm px-3 py-2 text-sm flex items-center gap-2">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  …
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Composer */}
          <form onSubmit={send} className="border-t border-[#A57F39]/15 bg-white p-2 flex gap-2">
            <input
              data-testid="chat-widget-input"
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={l.placeholder}
              className="flex-1 rounded-full border border-[#A57F39]/25 focus:border-[#A57F39] focus:outline-none px-4 py-2 text-sm text-[#0F1A2B]"
              disabled={sending}
            />
            <button
              type="submit"
              data-testid="chat-widget-send"
              disabled={sending || !input.trim()}
              className="h-9 w-9 rounded-full bg-[#A57F39] hover:bg-[#BD984D] disabled:opacity-50 text-white flex items-center justify-center"
              aria-label={l.send}
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </button>
          </form>
        </div>
      )}
    </>
  );
}
